import VariantPicker from '@theme/variant-picker';
import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, mediaQueryLarge } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  #fetchProductPageHandler = () => {
    if (!this.refs.quickAdd?.cachedProductHtml) {
      this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
    }
  };

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');
    this.#handleQuickAdd();

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
    mediaQueryLarge.addEventListener('change', this.#handleQuickAdd);

    this.addEventListener('click', this.navigateToProduct);
    this.#observeDetailHeight();

    // Preload the next image on the slideshow to avoid white flashes on previewImage
    setTimeout(() => {
      if (this.refs.slideshow?.isNested) {
        this.#preloadNextPreviewImage();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.navigateToProduct);
    this.#detailResizeObserver?.disconnect();
    this.#detailResizeObserver = undefined;
    cancelAnimationFrame(this.#detailHeightFrame);
    this.#detailHeightFrame = 0;
    this.removeEventListener('pointerdown', this.#prepareDetailHeight, true);
    this.removeEventListener('click', this.#prepareDetailHeight, true);
    this.removeEventListener('keydown', this.#prepareDetailHeight, true);
    document.fonts?.removeEventListener('loadingdone', this.#scheduleDetailHeight);
    this.#detailContent = null;
    this.#detailWidth = 0;
    this.#snNecklaceManualImage = false;
    this.style.removeProperty('--sn-card-default-height');
  }

  /** @type {HTMLElement | null} */
  #detailContent = null;
  /** @type {ResizeObserver | undefined} */
  #detailResizeObserver;
  #detailHeightFrame = 0;
  #detailWidth = 0;
  #measuringDetail = false;

  /** The marked, selectable non-variant image; hidden variant slides are never revealed for hover. */
  #getDetailSlide() {
    if (!this.hasAttribute('data-sn-detail-actions')) return null;
    const slide = this.refs.slideshow?.querySelector(
      'slideshow-slide[data-sn-card-detail]:not([hidden]):not([variant-image])'
    );
    return slide instanceof HTMLElement && slide.getAttribute('slide-id') ? slide : null;
  }

  #observeDetailHeight() {
    if (!this.#getDetailSlide()) return;
    const content = this.querySelector(':scope > .product-card__content');
    if (!(content instanceof HTMLElement)) return;
    this.#detailContent = content;
    this.#captureDetailHeight();
    this.#detailResizeObserver = new ResizeObserver(() => {
      const width = content.getBoundingClientRect().width;
      if (width <= 0) return;
      if (this.#getDetailSlide()?.getAttribute('aria-hidden') === 'false') {
        // A detail-state height is fixed: never feed it back into the default measurement.
        if (Math.abs(width - this.#detailWidth) > 0.5) this.#scheduleDetailHeight();
      } else {
        this.#captureDetailHeight();
      }
    });
    this.#detailResizeObserver.observe(content);
    this.addEventListener('pointerdown', this.#prepareDetailHeight, true);
    this.addEventListener('click', this.#prepareDetailHeight, true);
    this.addEventListener('keydown', this.#prepareDetailHeight, true);
    document.fonts?.addEventListener('loadingdone', this.#scheduleDetailHeight);
    document.fonts?.ready.then(() => {
      if (this.isConnected && this.#detailContent === content) this.#scheduleDetailHeight();
    });
    this.#scheduleDetailHeight();
  }

  #captureDetailHeight() {
    if (!this.#detailContent || this.#getDetailSlide()?.getAttribute('aria-hidden') === 'false') return;
    const { width, height } = this.#detailContent.getBoundingClientRect();
    if (width <= 0 || height <= 0) return;
    this.#detailWidth = width;
    const value = `${height}px`;
    if (this.style.getPropertyValue('--sn-card-default-height') !== value) {
      this.style.setProperty('--sn-card-default-height', value);
    }
  }

  /** Pointer/keyboard input precedes delegated arrow clicks; touch dragging also starts with this measurement. */
  #prepareDetailHeight = (event) => {
    if (event.target instanceof Element && event.target.closest('slideshow-component') === this.refs.slideshow) {
      this.#captureDetailHeight();
    }
  };

  #scheduleDetailHeight = () => {
    if (this.#detailHeightFrame || !this.#detailContent || this.#measuringDetail) return;
    this.#detailHeightFrame = requestAnimationFrame(this.#refreshDetailHeight);
  };

  #refreshDetailHeight = () => {
    this.#detailHeightFrame = 0;
    if (!this.isConnected || !this.#detailContent) return;
    const detail = this.#getDetailSlide();
    const restoreDetail = detail?.getAttribute('aria-hidden') === 'false';
    this.#measuringDetail = true;
    try {
      // Reflow the real default layout after width/font changes, then restore the preview before paint.
      if (restoreDetail) this.#resetDetailImage();
      this.#captureDetailHeight();
      const id = detail?.getAttribute('slide-id');
      if (restoreDetail && id) this.refs.slideshow?.select({ id }, undefined, { animate: false });
    } finally {
      this.#measuringDetail = false;
    }
  };

  #resetDetailImage() {
    const { slideshow } = this.refs;
    const detail = this.#getDetailSlide();
    if (!slideshow || !detail) return;
    const slides = slideshow.slides?.filter((slide) => !slide.hidden && slide !== detail) ?? [];
    const selectedId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const selected = selectedId ? slides.find((slide) => slide.getAttribute('slide-id') === selectedId) : null;
    const defaultSlide = slideshow.defaultSlide;
    const target = selected ?? (defaultSlide && slides.includes(defaultSlide) ? defaultSlide : slides[0]);
    const id = target?.getAttribute('slide-id');
    if (id) slideshow.select({ id }, undefined, { animate: false });
  }

  #preloadNextPreviewImage() {
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute('loading');
  }

  /**
   * Handles the quick add event.
   */
  #handleQuickAdd = () => {
    this.removeEventListener('pointerenter', this.#fetchProductPageHandler);
    this.removeEventListener('focusin', this.#fetchProductPageHandler);

    if (isDesktopBreakpoint()) {
      this.addEventListener('pointerenter', this.#fetchProductPageHandler);
      this.addEventListener('focusin', this.#fetchProductPageHandler);
    }
  };

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;
    this.#snNecklaceManualImage = false;
    this.#scheduleDetailHeight();
  };

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      this.refs.productCardLink.href = anchorElement.href;
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Hide the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    if (!this.variantPicker?.selectedOption) {
      return;
    }

    const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

    if (slideshow && selectedImageId) {
      const { slides = [] } = slideshow.refs;

      for (const slide of slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the variant picker component.
   * @returns {VariantPicker | null} The variant picker component.
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }
  /** @type {number | null} */
  #previousSlideIndex = null;
  #snNecklaceManualImage = false;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      this.#previousSlideIndex = event.detail.index;
    }
    if (event.target === this.refs.slideshow && event.detail.userInitiated === true && event.detail.trigger === 'select' && this.refs.cardGallery?.hasAttribute('data-sn-necklaces-card-arrows')) {
      // Returning to the default image re-enables the next hover preview.
      this.#snNecklaceManualImage = event.detail.slide !== this.#getHoverDefaultSlide();
      this.resetVariant.cancel();
    }
    const detail = this.#getDetailSlide();
    if (detail && event.target === this.refs.slideshow) {
      // The slideshow updates other slides after scrolling. End the scoped detail layout immediately on reset.
      detail.setAttribute('aria-hidden', String(event.detail.slide !== detail));
      if (event.detail.slide !== detail) this.#scheduleDetailHeight();
    }
  };

  /**
   * Previews a variant.
   * @param {string} id - The id of the variant to preview.
   */
  previewVariant(id) {
    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();
    slideshow.select({ id }, undefined, { animate: false });
  }

  // SANCTURA_STANDARD_PRODUCT_HOVER_START
  /** A visible default image remains the selected variant image when a picker is present. */
  #getHoverDefaultSlide() {
    const { slideshow } = this.refs;
    const slides = slideshow?.slides?.filter((slide) => !slide.hasAttribute('hidden')) ?? [];
    const selectedId = this.variantPicker?.selectedOption?.dataset.optionMediaId;
    const selected = selectedId ? slides.find((slide) => slide.getAttribute('slide-id') === selectedId) : null;
    return selected ?? (slides.includes(slideshow?.defaultSlide) ? slideshow.defaultSlide : slides[0]);
  }

  /** Preview the first distinct, visible generic photograph; never reveal another variant or video. */
  #getHoverSecondSlide() {
    const { slideshow } = this.refs;
    const primary = this.#getHoverDefaultSlide();
    return slideshow?.slides?.find((slide) =>
      slide !== primary && !slide.hasAttribute('hidden') && !slide.hasAttribute('variant-image') &&
      slide.classList.contains('product-media-container--image') && slide.getAttribute('slide-id')
    );
  }
  // SANCTURA_STANDARD_PRODUCT_HOVER_END

  /** @param {Event} event */
  previousNecklaceImage(event) {
    this.#selectNecklaceImage(-1, event);
  }

  /** @param {Event} event */
  nextNecklaceImage(event) {
    this.#selectNecklaceImage(1, event);
  }

  /**
   * Aligns the existing detail layout before an instant, finite native selection.
   * @param {-1 | 1} direction
   * @param {Event} event
   */
  #selectNecklaceImage(direction, event) {
    const { slideshow, cardGallery } = this.refs;
    if (!cardGallery?.hasAttribute('data-sn-necklaces-card-arrows') || !slideshow || slideshow.disabled) return;
    const button = event.target instanceof Element ? event.target.closest('button.slideshow-control') : null;
    if (!(button instanceof HTMLButtonElement) || button.disabled) return;
    if (button.getAttribute('ref') !== (direction < 0 ? 'previous' : 'next')) return;
    const arrows = button.closest('slideshow-arrows.sn-necklaces-card-arrows');
    if (!arrows || arrows.closest('.card-gallery') !== cardGallery || arrows.closest('slideshow-component') !== slideshow) return;
    const slides = slideshow.slides;
    const current = slideshow.current;
    if (!Array.isArray(slides) || !Number.isInteger(current)) return;
    const index = current + direction;
    if (index < 0 || index >= slides.length) return;
    const target = slides[index];
    const id = target?.getAttribute('slide-id');
    if (!id || typeof slideshow.select !== 'function') return;

    this.resetVariant.cancel();
    this.#captureDetailHeight();
    const detail = this.#getDetailSlide();
    if (detail) detail.setAttribute('aria-hidden', String(target !== detail));
    slideshow.select({ id }, event, { animate: false });
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (this.#snNecklaceManualImage) return;
    const { slideshow } = this.refs;

    if (!slideshow || event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    if (this.hasAttribute('data-sn-detail-actions')) {
      const id = this.#getDetailSlide()?.getAttribute('slide-id');
      if (!id) return;
      this.#captureDetailHeight();
      slideshow.select({ id }, undefined, { animate: false });
      return;
    }

    const target = this.#getHoverSecondSlide();
    const id = target?.getAttribute('slide-id');
    if (!id) return;
    slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} [event] - The pointer leaving the gallery or card.
   */
  resetImage(event) {
    if (this.#snNecklaceManualImage) return;
    if (event && event.pointerType !== 'mouse') return;
    const { slideshow } = this.refs;
    if (this.hasAttribute('data-sn-detail-actions')) {
      if (!slideshow || (event && event.pointerType !== 'mouse')) return;
      this.resetVariant.cancel();
      this.#resetDetailImage();
      return;
    }
    if (!slideshow) return;
    this.resetVariant.cancel();
    const id = this.#getHoverDefaultSlide()?.getAttribute('slide-id');
    if (id) slideshow.select({ id }, undefined, { animate: false });
  }

  /**
   * Resets the image to the variant image.
   */
  #resetVariant = () => {
    if (this.#snNecklaceManualImage) return;
    const { slideshow } = this.refs;

    if (!slideshow) return;

    if (this.#getDetailSlide()) {
      this.#resetDetailImage();
      return;
    }

    const defaultSlide = slideshow.defaultSlide;
    const slideId = defaultSlide?.getAttribute('slide-id');
    if (defaultSlide && slideshow.slides?.includes(defaultSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    } else if (!this.variantPicker?.selectedOption) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    const id = this.variantPicker.selectedOption.dataset.optionMediaId;
    if (!id) {
      slideshow.previous(undefined, { animate: false });
      return;
    }

    slideshow.select({ id }, undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    const interactiveElement = event.target.closest('button, input, label, select, a, [tabindex="1"]');

    // If the click was on an interactive element which is not the main link, do nothing.
    if (interactiveElement && interactiveElement !== this.refs.productCardLink) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }
    history.replaceState({}, '', url.toString());

    this.#navigateToURL(event, linkURL);
  };

  /**
   * Resets the variant.
   */
  resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
  /**
   * Shows all swatches.
   * @param {Event} [event] - The event that triggered the show all swatches.
   */
  showAllSwatches(event) {
    event?.preventDefault();

    const { overflowList } = this.refs;

    if (overflowList instanceof OverflowList) {
      overflowList.showAll();
    }
  }
}

if (!customElements.get('swatches-variant-picker-component')) {
  customElements.define('swatches-variant-picker-component', SwatchesVariantPickerComponent);
}

// SANCTURA_STATIC_PRODUCT_HOVER_START
// Static product cards share mouse previews without changing their Liquid, CSS, or navigation.
(() => {
  const binding = Symbol.for('sanctura.static-product-hover');
  if (document[binding]) return;
  document[binding] = true;

  const cardSelector = [
    'a.resource-card[data-resource-type="product"]',
    'a.product-card',
    'article[data-wuqu-recommendation]',
    'article[data-unified-recommendation]',
    'a.srp-product-card',
    'a.fea__product-card',
  ].join(',');
  const excludedSelector = [
    'product-card', 'cart-items-component', 'cart-drawer', 'cart-drawer-component',
    '.cart-items', '.cart-drawer', 'quick-add', 'quick-add-component', 'quick-add-dialog',
    'media-gallery', 'media-gallery-component',
    '[data-salon-stage]', '[data-unified-stage]', '[data-wuqu-stage]',
  ].join(',');
  const states = new WeakMap();
  const activeStates = new Set();
  const products = new Map();
  const preservedAttributes = ['src', 'srcset', 'sizes', 'alt', 'style', 'width', 'height', 'loading', 'data-media-id'];
  const nativeBraceletIds = new Map([
    ['classic-sacred-bloom-five-motif-bracelet-malachite', '15263792759147'],
    ['classic-sacred-bloom-five-motif-bracelet-tigers-eye', '15263792726379'],
    ['classic-sacred-bloom-five-motif-bracelet-onyx', '15263792660843'],
    ['classic-sacred-bloom-five-motif-bracelet-mother-of-pearl', '15263792628075'],
    ['classic-sacred-bloom-five-motif-bracelet-carnelian', '15263792595307'],
    ['solar-muse-citrine-bracelet', '15263580029291'],
  ]);
  const nativeBraceletProductIds = new Set(nativeBraceletIds.values());
  const nativeAttributes = [...preservedAttributes, 'class', 'data_max_resolution'];
  const normalDetailIds = new Map([
    ['solar-muse-citrine-bracelet', '15263580029291'],
    ['organic-muse-baroque-pearl-necklace', '15262608654699'],
    ['organic-muse-baroque-pearl-bracelet', '15210165829995'],
    ['aura-muse-purple-titanium-quartz-bracelet', '15210165862763'],
    ['earth-muse-turquoise-bracelet', '15210165895531'],
    ['earth-muse-turquoise-bracelet-副本', '15270555255147'],
    ['solar-muse-citrine-bracelet-副本', '15270555386219'],
  ]);

  function productIdentity(anchor) {
    try {
      const url = new URL(anchor.href, location.href);
      const match = url.pathname.match(/\/products\/([^/]+)\/?$/);
      if (url.origin !== location.origin || !match) return null;
      const handle = decodeURIComponent(match[1]);
      if (!handle || /[\s/\\?#]/.test(handle) || /\.(?:js|json)$/i.test(handle)) return null;
      return { handle, href: anchor.getAttribute('href'), variantId: url.searchParams.get('variant') };
    } catch {
      return null;
    }
  }

  function resolveCard(target) {
    if (!(target instanceof Element)) return null;
    const card = target.closest(cardSelector);
    if (!card || !card.isConnected || card.closest(excludedSelector)) return null;
    // Search is the intentional dialog exception; cart, quick-add and image dialogs remain excluded.
    const dialog = card.closest('dialog,dialog-component,[role="dialog"]');
    const searchOwner = dialog?.closest('dialog-component#search-modal');
    if (dialog && !(searchOwner && (dialog === searchOwner || (dialog.matches('dialog.search-modal__content') && dialog.parentElement === searchOwner)))) return null;
    const anchors = card instanceof HTMLAnchorElement ? [card] : Array.from(card.querySelectorAll('a[href]'));
    const linked = anchors.map((anchor) => ({ anchor, identity: productIdentity(anchor) })).filter((item) => item.identity);
    if (!linked.length || new Set(linked.map((item) => item.identity.handle)).size !== 1) return null;
    const link = linked.find((item) => item.anchor.querySelector('img'));
    if (!link || (card.dataset.productHandle && card.dataset.productHandle !== link.identity.handle)) return null;
    const images = Array.from(card.querySelectorAll('img'));
    const img = link.anchor.querySelector('img');
    if (!(img instanceof HTMLImageElement) || images.length !== 1 || img.closest(excludedSelector)) return null;
    const picture = img.closest('picture');
    if (picture && !(nativeBraceletIds.has(link.identity.handle) && card.matches('a.fea__product-card') && card.closest('.fea.fea--reference') && card.contains(picture) && img.parentElement === picture && picture.children.length === 1 && picture.style.display === 'contents' && !picture.querySelector('source'))) return null;
    if (card.querySelector('swatches-variant-picker-component,variant-picker,variant-picker-component')) return null;
    const box = img.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return null;
    return { card, anchor: link.anchor, img, ...link.identity };
  }

  function sourceUrl(value) {
    const source = value instanceof URL ? value.href : typeof value === 'string' ? value : value && typeof value === 'object' ? value.src || value.url : null;
    if (typeof source !== 'string' || !source) return null;
    try {
      const url = new URL(source, location.href);
      return url.protocol === 'https:' || url.protocol === 'http:' ? url : null;
    } catch {
      return null;
    }
  }

  function imageKey(value) {
    const url = sourceUrl(value);
    if (!url) return null;
    // Shopify can serve the same original from the shop domain and cdn.shopify.com.
    // Retain the complete filename and version: an _800x800 suffix may be part of an uploaded original.
    const match = url.pathname.match(/\/(files|products)\/([^/]+)$/);
    return match ? `${match[1]}/${match[2]}?v=${url.searchParams.get('v') || ''}` : null;
  }

  function nativeBraceletSource(product, state) {
    if (product.variants.length !== 1 || (state.variantId != null && state.variantId !== String(product.variants[0]?.id)) || product.requires_selling_plan || product.selling_plan_groups?.length) return null;
    const media = Array.isArray(product.media) ? product.media[1] : null;
    const preview = media?.preview_image;
    const source = sourceUrl(preview?.src || media?.src);
    const width = Number(preview?.width ?? media?.width);
    const height = Number(preview?.height ?? media?.height);
    if (media?.media_type !== 'image' || !/^[1-9]\d*$/.test(String(media.id)) || !Number.isInteger(width) || width <= 0 || !Number.isInteger(height) || height <= 0 || !source || source.protocol !== 'https:' || source.username || source.password) return null;
    if (source.origin !== location.origin && source.origin !== 'https://cdn.shopify.com') return null;
    if (!/^\/(?:cdn\/shop\/(?:files|t)\/|s\/files\/)/.test(source.pathname)) return null;
    const key = imageKey(source);
    if (!key || (media.src && imageKey(media.src) !== key)) return null;
    const variant = product.variants[0];
    if (!variant || typeof variant !== 'object' || !/^[1-9]\d*$/.test(String(variant.id))) return null;
    if (variant.featured_image != null && (!imageKey(variant.featured_image) || imageKey(variant.featured_image) === key || String(variant.featured_image.id) === String(media.id))) return null;
    const nativeWidth = 1946;
    const nativeHeight = Math.max(1, Math.round(nativeWidth * height / width));
    const atWidth = (size) => {
      const url = new URL(source.href);
      url.searchParams.set('width', String(size));
      return url.href;
    };
    const widths = [300, 450, 600, 750, 900, 1050, 1200, 1350, 1500, 1650, 1800, 1950, 2000, 2500, 3000, 3500, 4000, 5000].filter((size) => size <= nativeWidth);
    if (!widths.length) widths.push(nativeWidth);
    const renderedSize = Math.max(1, Math.ceil(state.img.getBoundingClientRect().width)) + 'px';
    const fallbackSizes = state.attributes.sizes?.replace(/^\s*auto\s*(?:,\s*)?/, '').trim() || renderedSize;
    return {
      native: true,
      preloadSizes: renderedSize,
      attributes: {
        src: atWidth(1946),
        srcset: widths.map((size) => atWidth(size) + ' ' + size + 'w').join(', '),
        sizes: 'auto, ' + fallbackSizes,
        alt: typeof media.alt === 'string' && media.alt ? media.alt : typeof product.title === 'string' ? product.title : state.attributes.alt || '',
        width: String(nativeWidth),
        height: String(nativeHeight),
        'data-media-id': String(media.id),
        data_max_resolution: atWidth(3840),
      },
    };
  }

  function annotatedRecommendationSource(product, state) {
    if (document.documentElement.lang !== 'en' || !state.card.matches('article[data-unified-recommendation]')) return null;
    const sourceId = state.card.getAttribute('data-unified-second-preview');
    const targets = {
      '15225363661163': '耳钉001',
      '15260067004779': 'luodi-stud-earrings',
    };
    const targetId = String(product.id);
    const targetHandle = targets[targetId];
    const root = state.card.closest('.sn-unified-pdp[data-product-handle]');
    const sourcePath = location.pathname.match(/\/products\/([^/]+)\/?$/);
    let sourceHandle;
    try { sourceHandle = sourcePath ? decodeURIComponent(sourcePath[1]) : null; } catch { return null; }
    if (!targetHandle || !/^[1-9]\d*$/.test(sourceId || '') || !sourceHandle || !root || !root.id.endsWith('-' + sourceId) || root.dataset.productHandle !== sourceHandle || !state.card.closest('.sn-unified-recommendations')) return null;
    if (state.card.dataset.productId !== targetId || state.card.dataset.productHandle !== targetHandle || state.handle !== targetHandle || product.handle !== targetHandle) return null;
    if (product.variants.length !== 1 || state.variantId != null || product.requires_selling_plan || product.selling_plan_groups?.length) return null;
    const approved = sourceUrl(state.card.getAttribute('data-unified-second-preview-url'));
    const media = Array.isArray(product.media) ? product.media[1] : null;
    const secondKey = imageKey(product.images[1]);
    if (!approved || approved.protocol !== 'https:' || approved.username || approved.password || !secondKey || imageKey(approved) !== secondKey) return null;
    if (media?.media_type !== 'image' || imageKey(media.preview_image?.src || media.src) !== secondKey) return null;
    if (secondKey === imageKey(state.attributes.src) || secondKey === imageKey(state.img.currentSrc)) return null;
    if (approved.origin !== location.origin && approved.origin !== 'https://cdn.shopify.com') return null;
    if (!/^\/(?:cdn\/shop\/(?:files|t)\/|s\/files\/)/.test(approved.pathname)) return null;
    return approved.href;
  }

  function previewSource(product, state) {
    if (!product || product.handle !== state.handle || !Array.isArray(product.images) || !Array.isArray(product.variants)) return null;
    // A truncated or structurally ambiguous variant list cannot safely identify generic photographs.
    if (!product.variants.length || product.variants.length >= 250 || (product.variants_count && product.variants_count > product.variants.length)) return null;
    const nativeId = nativeBraceletIds.get(state.handle);
    if (nativeId || nativeBraceletProductIds.has(String(product.id))) {
      return nativeId && String(product.id) === nativeId ? nativeBraceletSource(product, state) : null;
    }
    // Only the two annotated products in English PDP recommendations may use their existing second photograph.
    if (state.card.hasAttribute('data-unified-second-preview')) return annotatedRecommendationSource(product, state);
    // PDP recommendation previews use only verified white detail photographs.
    // An empty explicit URL intentionally keeps the white first image in place.
    if (state.img.hasAttribute('data-recommendation-white') && state.img.closest('.sn-unified-recommendations, .sn-wuqu-recommendations, .sn-salon .collection')) {
      if (state.img.getAttribute('data-recommendation-white') !== 'true' || product.variants.length !== 1 || state.variantId != null || product.requires_selling_plan || product.selling_plan_groups?.length) return null;
      const approved = sourceUrl(state.img.getAttribute('data-recommendation-white-hover'));
      if (!approved || approved.protocol !== 'https:') return null;
      if (approved.origin !== location.origin && approved.hostname !== 'cdn.shopify.com') return null;
      if (!/^\/(?:cdn\/shop\/(?:files|t)\/|s\/files\/)/.test(approved.pathname)) return null;
      return approved.href;
    }
    const images = product.images.map(sourceUrl).filter(Boolean);
    if (images.length < 2 || product.images.some((image) => typeof image !== 'string' || !imageKey(image))) return null;
    const primary = [state.attributes.src, state.img.currentSrc].map(imageKey).find((key) => key && images.some((image) => imageKey(image) === key));
    const variantImages = new Set();
    for (const variant of product.variants) {
      if (!variant || typeof variant !== 'object') return null;
      if (!Object.hasOwn(variant, 'featured_image')) {
        if (product.variants.length > 1) return null;
        continue;
      }
      if (variant.featured_image == null) continue;
      const key = imageKey(variant.featured_image);
      if (!key) return null;
      variantImages.add(key);
    }
    // Some verified single-variant cards use a theme asset as their first photograph.
    // Its catalogue fallback is exactly image two; never infer another variant or use image one.
    if (!primary && (product.variants.length !== 1 || state.variantId != null)) return null;
    const candidates = primary ? images.slice(1) : images.slice(1, 2);
    if (!primary && imageKey(candidates[0]) === imageKey(images[0])) return null;
    const imageMedia = Array.isArray(product.media) ? product.media.filter((media) => media?.media_type === 'image') : null;
    for (const image of candidates) {
      const key = imageKey(image);
      if (key === primary || variantImages.has(key)) continue;
      if (imageMedia && !imageMedia.some((media) => imageKey(media.src || media.preview_image?.src) === key)) continue;
      const width = Math.min(1440, Math.max(180, Math.ceil(state.img.getBoundingClientRect().width * (window.devicePixelRatio || 1))));
      image.searchParams.set('width', String(width));
      return image.href;
    }
    return null;
  }

  function productRequest(handle) {
    const root = window.Shopify?.routes?.root;
    if (typeof root !== 'string' || !root.startsWith('/') || !root.endsWith('/') || root.startsWith('//')) return Promise.resolve(null);
    const url = `${root}products/${encodeURIComponent(handle)}.js`;
    if (!products.has(url)) {
      // The request is shared; leaving one card invalidates its token, never another card's request.
      const request = fetch(url, { credentials: 'same-origin' })
        .then((response) => response.ok ? response.json() : null)
        .then((product) => product && product.handle === handle && Array.isArray(product.images) && Array.isArray(product.variants) ? product : null)
        .catch(() => null)
        .then((product) => {
          if (!product && products.get(url) === request) products.delete(url);
          return product;
        });
      products.set(url, request);
    }
    return products.get(url);
  }

  function stillCurrent(state) {
    if (!state.active || !state.card.isConnected || !state.img.isConnected || !state.card.contains(state.img) || !state.card.matches(':hover')) return false;
    const resolved = resolveCard(state.card);
    return resolved?.img === state.img && resolved.anchor === state.anchor && resolved.handle === state.handle && resolved.href === state.href;
  }

  function unchangedSource(state) {
    const names = nativeBraceletIds.has(state.handle) ? nativeAttributes : preservedAttributes;
    return names.every((name) => state.img.getAttribute(name) === state.attributes[name]);
  }

  function finish(state) {
    state.active = false;
    state.token += 1;
    activeStates.delete(state);
    const identity = productIdentity(state.anchor);
    if (state.nativeApplied) {
      if (state.card.contains(state.img) && identity?.handle === state.handle && identity.variantId === state.variantId && identity.href === state.href && Object.entries(state.nativeApplied).every(([name, value]) => state.img.getAttribute(name) === value)) {
        for (const name of Object.keys(state.nativeApplied)) {
          if (state.attributes[name] == null) state.img.removeAttribute(name);
          else state.img.setAttribute(name, state.attributes[name]);
        }
      }
      state.nativeApplied = null;
      state.applied = null;
      return;
    }
    if (state.applied && state.card.contains(state.img) && identity?.handle === state.handle && identity.variantId === state.variantId && state.img.getAttribute('data-media-id') === state.attributes['data-media-id'] && state.img.getAttribute('src') === state.applied && state.img.getAttribute('srcset') === null) {
      for (const name of ['src', 'srcset']) {
        if (state.attributes[name] == null) state.img.removeAttribute(name);
        else state.img.setAttribute(name, state.attributes[name]);
      }
    }
    state.applied = null;
  }

  async function start(resolved) {
    const previous = states.get(resolved.card);
    if (previous?.active) finish(previous);
    const state = {
      ...resolved,
      attributes: Object.fromEntries((nativeBraceletIds.has(resolved.handle) ? nativeAttributes : preservedAttributes).map((name) => [name, resolved.img.getAttribute(name)])),
      active: true,
      token: 1,
      applied: null,
    };
    states.set(state.card, state);
    activeStates.add(state);
    const token = state.token;
    try {
      const product = await productRequest(state.handle);
      if (state.token !== token || !stillCurrent(state) || !unchangedSource(state)) return;
      const source = previewSource(product, state);
      if (!source) return;
      const normalDetail = normalDetailIds.get(state.handle) === String(product.id);
      const preload = new Image();
      if (source.native) {
        preload.sizes = source.preloadSizes;
        preload.srcset = source.attributes.srcset;
      }
      preload.src = source.native ? source.attributes.src : source;
      if (typeof preload.decode === 'function') await preload.decode();
      else await new Promise((resolve, reject) => {
        if (preload.complete && preload.naturalWidth > 0) resolve();
        else { preload.onload = resolve; preload.onerror = reject; }
      });
      if (!preload.naturalWidth || state.token !== token || !stillCurrent(state) || !unchangedSource(state)) return;
      if (source.native) {
        for (const [name, value] of Object.entries(source.attributes)) state.img.setAttribute(name, value);
        state.img.style.setProperty('padding', '12px 16px 0', 'important');
        state.img.style.setProperty('object-fit', normalDetail ? 'contain' : 'cover', 'important');
        state.img.style.setProperty('object-position', 'center', 'important');
        state.img.style.setProperty('transform', 'none', 'important');
        state.nativeApplied = Object.fromEntries([...Object.keys(source.attributes), 'style'].map((name) => [name, state.img.getAttribute(name)]));
        state.applied = source.attributes.src;
        return;
      }
      state.applied = source;
      state.img.removeAttribute('srcset');
      state.img.setAttribute('src', source);
      if (normalDetail) {
        state.img.style.setProperty('object-fit', 'contain', 'important');
        state.img.style.setProperty('object-position', 'center', 'important');
        state.img.style.setProperty('transform', 'none', 'important');
        state.nativeApplied = Object.fromEntries(['src', 'srcset', 'style', 'data-media-id'].map((name) => [name, state.img.getAttribute(name)]));
      }
    } catch {
      // Failed API/image reads leave the original image and navigation intact.
    } finally {
      if (!state.applied) finish(state);
    }
  }

  document.addEventListener('pointerover', (event) => {
    if (event.pointerType !== 'mouse') return;
    for (const state of activeStates) if (!state.card.isConnected) finish(state);
    const resolved = resolveCard(event.target);
    if (!resolved || (event.relatedTarget instanceof Node && resolved.card.contains(event.relatedTarget))) return;
    start(resolved);
  }, true);

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType !== 'mouse') return;
    for (const state of activeStates) {
      if (event.target instanceof Node && state.card.contains(event.target) && !(event.relatedTarget instanceof Node && state.card.contains(event.relatedTarget))) finish(state);
    }
  }, true);

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') return;
    // Only owned static previews are cleaned up; native touch/pen carousel frames are untouched.
    for (const state of activeStates) finish(state);
  }, true);
  window.addEventListener('blur', () => { for (const state of activeStates) finish(state); });
  window.addEventListener('pagehide', () => { for (const state of activeStates) finish(state); });
})();
// SANCTURA_STATIC_PRODUCT_HOVER_END
